import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setAuthToken } from '../../api/client';
import { useApp } from '../../context/usePortal';
import { useGeo } from './api/hooks';
import { standaloneReturnUrl } from './flow/exitUrl';
import { useRegistrationFlow } from './flow/useRegistrationFlow';
import { CreateAccountScreen } from './screens/CreateAccountScreen';
import { DryRunResult } from './screens/DryRunResult';
import { PaymentMethodAdded } from './screens/PaymentMethodAdded';
import { PaymentMethodError } from './screens/PaymentMethodError';
import { RegistrationSuccess } from './screens/RegistrationSuccess';
import { SelectPaymentMethod } from './screens/SelectPaymentMethod';
import { useLinkMethod } from './useLinkMethod';
import { notifyExternalRegistrationResult } from './utils/external-session-notify';

const FLOW_PATH = '/register/payment';
const METHOD_ADDED_PATH = '/register/method-added';
const METHOD_FAILED_PATH = '/register/method-failed';

const CARDS_ONLY_COUNTRIES = ['SG', 'PH'];

/** The gateway's failure as carried on the method-failed URL. */
function failureFromQuery(search: string) {
  const q = new URLSearchParams(search);
  return {
    code: q.get('code'),
    desc: q.get('desc'),
    cancelled: q.get('cancelled') === '1'
  };
}

const isGatewayReturn = (pathname: string) =>
  pathname === METHOD_ADDED_PATH || pathname === METHOD_FAILED_PATH;

export default function RegistrationFlowPage() {
  const { context, step, send } = useRegistrationFlow();
  const { refreshUser } = useApp();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const link = useLinkMethod(context, send);
  const needsMethods = step === 'selectMethod';
  const geo = useGeo(needsMethods);
  const handledReturn = useRef(false);
  const [dryRunPassed, setDryRunPassed] = useState(false);

  // A gateway return in a tab that lost its context (another tab, cleared storage) still shows
  // the outcome; it is not sent back to the start.
  useEffect(() => {
    if (!context && !isGatewayReturn(pathname)) {
      navigate('/register', { replace: true });
    }
  }, [context, pathname, navigate]);

  // The gateway sends the visitor back to one of these URLs; each is applied
  // to the flow once and then replaced so a reload or Back cannot apply it again.
  useEffect(() => {
    if (!context || handledReturn.current) return;
    if (pathname === METHOD_ADDED_PATH) {
      handledReturn.current = true;
      send('methodAdded');
      navigate(FLOW_PATH, { replace: true });
    } else if (pathname === METHOD_FAILED_PATH) {
      handledReturn.current = true;
      send('methodFailed', { failure: failureFromQuery(search) });
      navigate(FLOW_PATH, { replace: true });
    }
  }, [context, pathname, search, send, navigate]);

  if (!context) {
    // A gateway return in a tab without the stored registration: the return URL may still
    // name the checkout to go back to; without it, the way out is the AyeFace home page.
    const goHome = () => navigate('/', { replace: true });
    if (pathname === METHOD_ADDED_PATH) {
      const back = standaloneReturnUrl(search, 'success');
      return back ? (
        <PaymentMethodAdded
          countdownSeconds={5}
          onCountdownEnd={() => window.location.assign(back)}
          onBackToCheckout={() => window.location.assign(back)}
        />
      ) : (
        <PaymentMethodAdded onGoHome={goHome} />
      );
    }
    if (pathname === METHOD_FAILED_PATH) {
      const back = standaloneReturnUrl(search, 'failed');
      return (
        <PaymentMethodError
          mode="standalone"
          failure={failureFromQuery(search)}
          {...(back
            ? { onBackToCheckout: () => window.location.assign(back) }
            : { onGoHome: goHome })}
        />
      );
    }
    return null;
  }
  if (pathname !== FLOW_PATH) return null;

  const { entry } = context;
  const mode = entry === 'checkoutOptional' ? 'optional' : 'required';

  switch (step) {
    case 'createAccount':
      if (dryRunPassed) {
        return <DryRunResult onBack={() => setDryRunPassed(false)} />;
      }
      return (
        <CreateAccountScreen
          context={context}
          onDryRun={() => setDryRunPassed(true)}
          onCreated={({ accessToken }) => {
            if (entry === 'direct') {
              localStorage.setItem('token', accessToken);
              localStorage.setItem('LoggedIn', 'true');
              setAuthToken(accessToken);
              // Load the new account into the app, as a sign-in does, so home greets them by name.
              refreshUser();
              const fromExternalSession = !!context.externalSessionId;
              send('accountCreated', { accessToken: null });
              if (fromExternalSession) {
                notifyExternalRegistrationResult('success');
              }
            } else {
              // A checkout or OngLaye visitor is not signed in to the portal:
              // the token only authorises linking a payment method.
              send('accountCreated', { accessToken });
            }
          }}
        />
      );
    case 'success':
      return (
        <RegistrationSuccess
          countdownSeconds={entry === 'checkoutOptional' ? 10 : undefined}
          onAddNow={() => send('addNow')}
          onLater={() => send('later')}
          onCountdownEnd={() => send('countdownEnded')}
        />
      );
    case 'selectMethod':
      return (
        <SelectPaymentMethod
          mode={mode}
          store={context.checkout}
          amount={context.checkout?.amount}
          currency={context.checkout?.currency}
          busy={link.busy}
          cardsOnly={CARDS_ONLY_COUNTRIES.includes(geo.data?.countryCode ?? '')}
          eWalletEnabled={
            process.env.REACT_APP_FEATURE_EWALLET_ENABLED === 'true'
          }
          onPick={link.pick}
          onExit={() => send('exit')}
        />
      );
    case 'methodError':
      return (
        <PaymentMethodError
          mode={mode}
          failure={
            context.account?.failure ?? {
              code: null,
              desc: null,
              cancelled: false
            }
          }
          onRetry={() => send('retry')}
          onExit={() => send('exit')}
        />
      );
    case 'methodAdded':
      return (
        <PaymentMethodAdded
          countdownSeconds={5}
          onCountdownEnd={() => send('countdownEnded')}
        />
      );
  }
}
